import {
  type AiSummaryInteraction,
  beginAiSummaryInteraction,
  getDatabaseContainerType,
  type AiFieldRunScope,
  type AiFieldRunSource,
  type AiFieldRunTriggerType,
  useAnalytics as getAnalyticsClient
} from '@databases/analytics';
import { logError } from '@databases/logs';
import { runDatabaseAiField } from '@databases/rovo-bridge';
import type { AiSummaryFieldConfig } from '@databases/types/database';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  prepareAiFieldGeneration,
  prepareAiFieldRefresh
} from '../prepare-manual-ai-field-refresh.ts';
import {
  runManualAiFieldRefresh,
  type ManualAiFieldRefreshAdapter
} from '../run-manual-ai-field-refresh.ts';

import {
  type AiSummaryFieldRunOperation,
  buildAiSummaryPrompt
} from './ai-summary-prompts.ts';

export { hasUserEditedAiSummaryCells } from './has-user-edited-ai-summary-cells.ts';

type RunAiSummaryFieldBaseOptions = {
  abortSignal?: AbortSignal;
  contentId: string;
  fieldId: string;
  targetFieldName: string;
  config: AiSummaryFieldConfig;
  locale: string;
  viewId: string;
  triggerType: AiFieldRunTriggerType;
  runScope: AiFieldRunScope;
  source: AiFieldRunSource;
};

type RunAiSummaryFieldAnalyticsOptions = RunAiSummaryFieldBaseOptions & {
  operation: AiSummaryFieldRunOperation;
};

export type RunAiSummaryFieldOptions = RunAiSummaryFieldBaseOptions &
  (
    | {
        operation: Exclude<
          AiSummaryFieldRunOperation,
          'single-entry-generate' | 'multiple-entry-generate'
        >;
      }
    | {
        operation: 'single-entry-generate';
        entryId: string;
      }
  );

export type RunManualAiSummaryRefreshOptions = RunAiSummaryFieldBaseOptions & {
  viewId: string;
} & (
    | {
        operation: 'refresh-entire-column' | 'refresh-empty-entries';
      }
    | {
        operation: 'single-entry-generate';
        entryId: string;
      }
    | {
        operation: 'multiple-entry-generate';
        entryIds: readonly string[];
      }
  );

const isAiSummaryEnabled = (): boolean =>
  getExperimentValue('databasesFrontendAiFieldsSummary', 'enabled', {
    fireExperimentExposure: false
  });

const logAndRethrow = (error: unknown): never => {
  logError(
    'Failed to generate AI Summary field',
    error instanceof Error ? error : new Error(String(error))
  );
  throw error;
};

const runWithAnalytics = async (
  options: RunAiSummaryFieldAnalyticsOptions,
  run: (traceContext: AiSummaryInteraction['traceContext']) => Promise<boolean>
): Promise<void> => {
  const analytics = getAnalyticsClient();
  const interaction = beginAiSummaryInteraction(
    options.contentId,
    `field:${options.fieldId}`,
    options.source
  );
  const eventData = {
    contentId: options.contentId,
    databaseContainer: getDatabaseContainerType(),
    fieldId: options.fieldId,
    aiFieldType: 'summary' as const,
    runId: globalThis.crypto.randomUUID(),
    triggerType: options.triggerType,
    operation: options.operation,
    runScope: options.runScope,
    source: options.source
  };

  void analytics.sendAiFieldRunStartedEvent(eventData);

  try {
    const didGenerateResult = await run(interaction.traceContext);
    if (didGenerateResult) {
      void analytics.sendAiFieldRunCompletedEvent(eventData);
      interaction.resultViewed();
    } else {
      interaction.endResultView();
    }
  } catch (error) {
    interaction.endResultView();
    const errorCode =
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      typeof error.code === 'string'
        ? error.code
        : undefined;
    void analytics.sendAiFieldRunFailedEvent({
      ...eventData,
      errorCategory: error instanceof Error ? error.name : 'unknown',
      errorCode
    });
    return logAndRethrow(error);
  }
};

export async function runAiSummaryField(
  options: RunAiSummaryFieldOptions
): Promise<void> {
  if (!isAiSummaryEnabled()) {
    return;
  }

  await runWithAnalytics(options, async (traceContext) => {
    options.abortSignal?.throwIfAborted();
    let didPersistGeneratedSummary = false;
    const scope =
      options.operation === 'single-entry-generate'
        ? ({ kind: 'entry', entryId: options.entryId } as const)
        : options.operation === 'refresh-empty-entries'
          ? ({ kind: 'empty-target-field-entries' } as const)
          : ({ kind: 'all-entries' } as const);
    const preparation = await prepareAiFieldGeneration(
      options.contentId,
      options.fieldId,
      scope,
      { viewId: options.viewId, config: options.config }
    );
    options.abortSignal?.throwIfAborted();
    const request = {
      ...(options.abortSignal !== undefined && { abortSignal: options.abortSignal }),
      contentId: options.contentId,
      entryFieldValuesSnapshot: preparation.requestContext,
      generationWriteContext: {
        ...preparation.generationWriteContext,
        onGeneratedValueCommitted: () => {
          didPersistGeneratedSummary = true;
        }
      },
      locale: options.locale,
      traceContext,
      prompt: buildAiSummaryPrompt({
        operation: options.operation,
        targetFieldName: options.targetFieldName,
        config: options.config
      })
    };

    if (options.operation === 'single-entry-generate') {
      await runDatabaseAiField({
        ...request,
        target: {
          aiFieldType: 'summary',
          fieldId: options.fieldId,
          scope: 'entry',
          entryId: options.entryId
        }
      });
      return didPersistGeneratedSummary;
    }

    await runDatabaseAiField({
      ...request,
      contextScope: {
        kind: 'entry-ids',
        entryIds: preparation.eligibleEntryIds
      },
      target: {
        aiFieldType: 'summary',
        fieldId: options.fieldId,
        scope: 'field'
      }
    });
    return didPersistGeneratedSummary;
  });
}

type AiSummaryGenerationWriteContext = Awaited<
  ReturnType<typeof prepareAiFieldRefresh>
>['generationWriteContext'];
type AiSummaryRequestContext = Awaited<
  ReturnType<typeof prepareAiFieldRefresh>
>['requestContext'];

const createAiSummaryManualRefreshAdapter = (
  traceContext: AiSummaryInteraction['traceContext'],
  onGeneratedValueCommitted: () => void
): ManualAiFieldRefreshAdapter<
  RunManualAiSummaryRefreshOptions,
  AiSummaryGenerationWriteContext,
  AiSummaryRequestContext
> => ({
  aiFieldType: 'summary',
  getAwarenessScope: (options) =>
    options.operation === 'single-entry-generate'
      ? { kind: 'entry', entryId: options.entryId }
      : options.operation === 'multiple-entry-generate'
        ? { kind: 'entries', entryIds: options.entryIds }
        : {
            kind: 'field',
            operation:
              options.operation === 'refresh-empty-entries'
                ? 'empty-target-entries'
                : 'all-entries'
          },
  prepare: (options) => {
    const scope =
      options.operation === 'single-entry-generate'
        ? ({ kind: 'entry', entryId: options.entryId } as const)
        : options.operation === 'multiple-entry-generate'
          ? ({ kind: 'entries', entryIds: options.entryIds } as const)
          : options.operation === 'refresh-empty-entries'
            ? ({ kind: 'empty-target-field-entries' } as const)
            : ({ kind: 'all-entries' } as const);

    return prepareAiFieldRefresh(options.contentId, options.fieldId, scope, {
      viewId: options.viewId,
      config: options.config
    });
  },
  runEligibleEntries: async (
    options,
    eligibleEntryIds,
    generationWriteContext,
    requestContext
  ) => {
    const request = {
      ...(options.abortSignal !== undefined && { abortSignal: options.abortSignal }),
      contentId: options.contentId,
      entryFieldValuesSnapshot: requestContext,
      generationWriteContext: {
        ...generationWriteContext,
        onGeneratedValueCommitted
      },
      locale: options.locale,
      traceContext,
      prompt: buildAiSummaryPrompt({
        operation: options.operation,
        targetFieldName: options.targetFieldName,
        config: options.config
      })
    } as const;

    if (options.operation === 'single-entry-generate') {
      await runDatabaseAiField({
        ...request,
        target: {
          aiFieldType: 'summary',
          fieldId: options.fieldId,
          scope: 'entry',
          entryId: options.entryId
        }
      });
      return;
    }

    if (options.operation === 'multiple-entry-generate') {
      await runDatabaseAiField({
        ...request,
        target: {
          aiFieldType: 'summary',
          fieldId: options.fieldId,
          scope: 'entries',
          entryIds: eligibleEntryIds
        }
      });
      return;
    }

    await runDatabaseAiField({
      ...request,
      contextScope: { kind: 'entry-ids', entryIds: eligibleEntryIds },
      target: {
        aiFieldType: 'summary',
        fieldId: options.fieldId,
        scope: 'field'
      }
    });
  }
});

export async function runManualAiSummaryRefresh(
  options: RunManualAiSummaryRefreshOptions
): Promise<void> {
  if (!isAiSummaryEnabled()) {
    return;
  }

  await runWithAnalytics(options, async (traceContext) => {
    let didPersistGeneratedSummary = false;
    const didRun = await runManualAiFieldRefresh(
      options,
      createAiSummaryManualRefreshAdapter(traceContext, () => {
        didPersistGeneratedSummary = true;
      })
    );
    return didRun && didPersistGeneratedSummary;
  });
}
